import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box,
  Grid, CircularProgress, List, ListItem, Chip, IconButton, Avatar, Divider, TextField
} from '@mui/material';
import API_ENDPOINTS from '../config/apiConfig';
import { useTranslation } from '../i18n/i18n';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';

export default function AdminUserDetailsModal({ open, onClose, userEmail, fullname, position, onDeleted }) {
  const { t } = useTranslation();
  const [personalInfo, setPersonalInfo] = useState(null);
  const [address, setAddress] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [requiredDocs, setRequiredDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requiredPersonalInfoFields, setRequiredPersonalInfoFields] = useState([]);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');

  useEffect(() => {
    if (!open || !userEmail) return;
    setLoading(true);
    Promise.all([
      fetch(`${API_ENDPOINTS.BASEURL}/api/user-documents/personalinfo/${userEmail}`, { credentials: 'include' }).then(res => res.json()),
      fetch(`${API_ENDPOINTS.BASEURL}/api/user-documents/${userEmail}`, { credentials: 'include' }).then(res => res.json()),
      fetch(`${API_ENDPOINTS.BASEURL}/api/required-documents`, { credentials: 'include' }).then(res => res.json()),
      fetch(`${API_ENDPOINTS.BASEURL}/api/required-personalinfo`, { credentials: 'include' }).then(res => res.ok ? res.json() : [])
    ]).then(([info, docs, reqDocs, personalInfoFields]) => {
      setPersonalInfo(info.personalinfo);
      setAddress(info.address);
      setDocuments(docs);
      setRequiredDocs(reqDocs);
      if (Array.isArray(personalInfoFields) && personalInfoFields.length > 0) {
        setRequiredPersonalInfoFields(personalInfoFields);
      }
      setLoading(false);
    });
  }, [open, userEmail]);

  const handlePreview = (doc) => setPreviewDoc(doc);
  const handleClosePreview = () => setPreviewDoc(null);

  const previewUrl = previewDoc
    ? `${API_ENDPOINTS.BASEURL}/api/user-documents/download/${userEmail}/${previewDoc.filename}?preview=true`
    : '';
  const downloadUrl = previewDoc
    ? `${API_ENDPOINTS.BASEURL}/api/user-documents/download/${userEmail}/${previewDoc.filename}`
    : '';

  const uploadedDocsMap = {};
  documents.forEach(doc => {
    uploadedDocsMap[doc.key] = doc;
  });

  const handleConfirmDelete = async () => {
    if (!userEmail) return;
    try {
      const res = await fetch(`${API_ENDPOINTS.BASEURL}/api/users/${encodeURIComponent(userEmail)}/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ confirm: true, reason: deleteReason })
      });
      if (res.ok) {
        setOpenDeleteDialog(false);
        setDeleteReason('');
        if (onDeleted) onDeleted(userEmail);
        onClose && onClose();
      }
    } catch (err) {
      console.error('Delete error', err);
      setOpenDeleteDialog(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main', color: 'white', fontSize: '1rem', fontWeight: 600 }}>
              {(fullname || '?').charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {fullname}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {userEmail}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton onClick={() => setOpenDeleteDialog(true)} size="small" color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 0 }}>
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Loading user details...
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Basic Info Section */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', color: 'white' }}>
                    <PersonIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('details')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                      {t('name')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{fullname}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                      {t('email')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{userEmail}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                      {t('position')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{position}</Typography>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Personal Info Section */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', color: 'white' }}>
                    <PersonIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('personaldocs_title_personal_info')}
                  </Typography>
                </Box>
                {personalInfo && requiredPersonalInfoFields.length > 0 ? (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                    {requiredPersonalInfoFields.map(fieldDef => {
                      const labelKey = `personaldocs_field_${fieldDef.key}`;
                      const translatedLabel = t(labelKey);
                      const label = (translatedLabel && translatedLabel !== labelKey) ? translatedLabel : fieldDef.label;
                      const rawVal = personalInfo[fieldDef.key];
                      const isSelect = fieldDef.validation && fieldDef.validation.type === 'select';
                      const displayVal = rawVal
                        ? (isSelect ? (t(rawVal) !== rawVal ? t(rawVal) : rawVal) : rawVal)
                        : '—';
                      return (
                        <Box key={fieldDef.key}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                            {label}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: rawVal ? 'text.primary' : 'text.secondary' }}>
                            {displayVal}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {t('personaldocs_title_personal_info')} — {t('not_available')}
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Address Section */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', color: 'white' }}>
                    <HomeIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('personaldocs_title_address')}
                  </Typography>
                </Box>
                {address ? (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                    <Box sx={{ gridColumn: { sm: 'span 3' } }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                        {t('personaldocs_field_street')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{address.street || '—'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                        {t('personaldocs_field_numExt')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{address.numExt || '—'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                        {t('personaldocs_field_numInt')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{address.numInt || '—'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                        {t('personaldocs_field_cp')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{address.cp || '—'}</Typography>
                    </Box>
                    <Box sx={{ gridColumn: { sm: 'span 2' } }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                        {t('personaldocs_field_neigborhood')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{address.neigborhood || '—'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                        {t('personaldocs_field_city')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{address.city || '—'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                        {t('personaldocs_field_state')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{address.state || '—'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}>
                        {t('personaldocs_field_country')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{address.country || '—'}</Typography>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {t('not_available')}
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Documents Section */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', color: 'white' }}>
                    <DescriptionIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t('personaldocs_title_documents')}
                  </Typography>
                </Box>
                {requiredDocs.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {t('not_available')}
                  </Typography>
                ) : (
                  <List disablePadding>
                    {requiredDocs.map(reqDoc => {
                      const uploadedDoc = uploadedDocsMap[reqDoc.key];
                      const transKey = `personaldocs_doc_${reqDoc.key}`;
                      const translated = t(transKey);
                      const title = (translated && translated !== transKey) ? translated : (reqDoc.label || reqDoc.key);
                      return (
                        <ListItem
                          key={reqDoc.key}
                          sx={{
                            px: 1.5,
                            py: 1.5,
                            mb: 1,
                            borderRadius: 1.5,
                            border: 1,
                            borderColor: 'divider',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                            <Avatar sx={{ width: 36, height: 36, bgcolor: uploadedDoc ? 'success.main' : 'grey.100', color: uploadedDoc ? 'white' : 'grey.500' }}>
                              <InsertDriveFileIcon sx={{ fontSize: 18 }} />
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {title}
                              </Typography>
                              {uploadedDoc && (
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(uploadedDoc.uploaded_at).toLocaleDateString()}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                            <Chip
                              label={uploadedDoc ? t('personaldocs_status_uploaded') : t('personaldocs_status_missing')}
                              size="small"
                              color={uploadedDoc ? 'success' : 'default'}
                              sx={{ fontWeight: 500, height: 24 }}
                            />
                            {uploadedDoc && (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={() => handlePreview(uploadedDoc)}
                                sx={{ textTransform: 'none' }}
                              >
                                {t('personaldocs_button_view_file')}
                              </Button>
                            )}
                          </Box>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2.5, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog open={!!previewDoc} onClose={handleClosePreview} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('personaldocs_dialog_preview_title')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {previewDoc?.filename}
            </Typography>
          </Box>
          <IconButton onClick={handleClosePreview} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: 'grey.50' }}>
          {previewDoc?.filename?.toLowerCase().endsWith('.pdf') ? (
            <iframe
              src={previewUrl}
              title="PDF Preview"
              width="100%"
              height="500px"
              style={{ border: 0 }}
            />
          ) : (
            <img
              src={previewUrl}
              alt={previewDoc?.filename}
              style={{ maxWidth: '100%', maxHeight: 500, display: 'block', margin: '0 auto' }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={handleClosePreview} sx={{ textTransform: 'none' }}>
            {t('close')}
          </Button>
          <Button
            onClick={() => window.open(downloadUrl, '_blank')}
            variant="contained"
            startIcon={<DownloadIcon />}
            sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
          >
            {t('download')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => { setOpenDeleteDialog(false); setDeleteReason(''); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('confirm_delete')}
          </Typography>
          <IconButton onClick={() => { setOpenDeleteDialog(false); setDeleteReason(''); }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('confirm_delete_prompt')} {fullname}?
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label={t('reason_optional')}
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={() => { setOpenDeleteDialog(false); setDeleteReason(''); }} sx={{ textTransform: 'none' }}>
            {t('cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
          >
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
